import React from 'react'
import { Button, Group, Stack, Textarea } from '@mantine/core'

import { CrFormHandle } from '@/components/Event/CrForm/CrFormStep'
import { useFormattedCurrentDateParts } from '@/components/Event/NewScript/hooks/useFormattedCurrentDateParts'
import { Event } from '@/types'
import { getEventTimezone } from '@/utils/time'

// One combined, time-ordered timestamps log per witness (mirrors the DR Script):
// "Go on/off record" and "Exhibit shown" both append a current-time line to a single
// box bound to events_feedbacks[witnessIndex].timestamp_log.
export const TimestampsControl: React.FC<{
  event: Event
  form: CrFormHandle
  witnessIndex: number
}> = ({ event, form, witnessIndex }) => {
  const dateParts = useFormattedCurrentDateParts(getEventTimezone(event))
  const prefix = `events_feedbacks.${witnessIndex}`
  const witness = form.values.events_feedbacks?.[witnessIndex]
  const onRecord = witness?.on_record ?? false

  const append = (text: string) => {
    const line = `${dateParts.formattedTimeWithTimezone} — ${text}`
    const current = witness?.timestamp_log ?? ''
    form.setFieldValue(
      `${prefix}.timestamp_log`,
      current ? `${current}\n${line}` : line,
    )
  }

  return (
    <Stack spacing="xs">
      <Group spacing="sm">
        <Button
          variant={onRecord ? 'filled' : 'outline'}
          color={onRecord ? 'red' : 'indigo'}
          onClick={() => {
            append(onRecord ? 'Off the record' : 'On the record')
            form.setFieldValue(`${prefix}.on_record`, !onRecord)
          }}
        >
          {onRecord ? 'Go off the record' : 'Go on the record'}
        </Button>
        <Button
          variant="outline"
          color="indigo"
          onClick={() => append('Exhibit shown')}
        >
          Exhibit shown
        </Button>
      </Group>
      <Textarea
        label="Timestamps"
        description="One timestamp per row (time — event), ordered by time."
        minRows={4}
        autosize
        {...form.getInputProps(`${prefix}.timestamp_log`)}
        value={witness?.timestamp_log ?? ''}
      />
    </Stack>
  )
}
